import React, { useEffect, useState } from 'react';
import { AlertTriangle, ClipboardList, UserCog, Ticket } from 'lucide-react';
import { agentAPI } from '@/lib/api';
import { statusesForStage } from '@/lib/contracting/statusMapping';
import { AttentionCard } from './AttentionCard';

/**
 * AgencyAttentionRequired — top operational band of the Agency Command
 * Center. Mirrors the KR AttentionRequired pattern but scoped to the
 * logged-in agency's downline.
 *
 * Data sources (all pre-existing, agency-scoped by the backend):
 *   1. Missing Information       → sum of `stats.requests_by_status[s]` for
 *                                  every raw status in `statusesForStage('missing')`.
 *   2. Pending Contract Reviews  → sum of `stats.requests_by_status[s]` for
 *                                  every raw status in `statusesForStage('review')`.
 *                                  Same taxonomy the pipeline tiles use — one
 *                                  source of truth.
 *   3. Agent Onboarding Issues   → self-fetched from `agentAPI.list()` and
 *                                  filtered to `status='Onboarding'`.
 *                                  (The `/workflows/agent-onboarding/runs`
 *                                  endpoint is KR-only, so we surface the
 *                                  agency-visible proxy: agents currently in
 *                                  Onboarding state.)
 *   4. Support Tickets           → `stats.open_tickets`.
 *
 * Rules (per Phase 20.5 spec):
 *   • Cards with count === 0 are omitted.
 *   • If all four are zero after loading, the empty state renders:
 *     "Nothing needs attention 🎉".
 *   • Returns null while any source is still loading — no empty-state flash.
 */
export const AgencyAttentionRequired = ({
  requestsByStatus = {},
  openTicketsCount = 0,
  parentLoading = false,
}) => {
  const [onboardingCount, setOnboardingCount] = useState(0);
  const [agentsLoading, setAgentsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await agentAPI.list({ status: 'Onboarding', page_size: 500 });
        if (cancelled) return;
        // `agentAPI.list` may return either a paginated envelope or a raw array.
        const payload = res?.data;
        const items = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
            ? payload.items
            : [];
        // Belt-and-suspenders: filter client-side in case the backend ignores
        // the status param for an agency-scoped caller.
        const count = items.filter(
          (a) => (a?.agent_status || a?.status || '').toLowerCase() === 'onboarding',
        ).length;
        setOnboardingCount(count);
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to load onboarding agents for attention band:', e);
      } finally {
        if (!cancelled) setAgentsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (parentLoading || agentsLoading) return null;

  const sumByStage = (stage) =>
    statusesForStage(stage).reduce(
      (total, statusKey) => total + (Number(requestsByStatus?.[statusKey]) || 0),
      0,
    );

  const missingInfoCount = sumByStage('missing');
  const pendingReviewsCount = sumByStage('review');

  const cards = [
    {
      key: 'missing-info',
      title: 'Missing Information',
      count: missingInfoCount,
      description: 'Contracting requests waiting on additional details from your team.',
      icon: AlertTriangle,
      href: '/requests?stage=missing',
      actionLabel: 'View Requests',
      severity: 'warning',
      testId: 'agency-attention-card-missing-info',
    },
    {
      key: 'pending-reviews',
      title: 'Pending Contract Reviews',
      count: pendingReviewsCount,
      description: 'Requests in carrier review or awaiting release.',
      icon: ClipboardList,
      href: '/requests?stage=review',
      actionLabel: 'Review requests',
      severity: 'info',
      testId: 'agency-attention-card-pending-reviews',
    },
    {
      key: 'onboarding-issues',
      title: 'Agent Onboarding',
      count: onboardingCount,
      description: 'Agents currently in onboarding — help them get to Active.',
      icon: UserCog,
      href: '/agents?status=Onboarding',
      actionLabel: 'View agents',
      severity: 'critical',
      testId: 'agency-attention-card-onboarding',
    },
    {
      key: 'support-tickets',
      title: 'Support Tickets',
      count: openTicketsCount,
      description: 'Open and in-progress tickets from your team.',
      icon: Ticket,
      href: '/tickets',
      actionLabel: 'View tickets',
      severity: 'info',
      testId: 'agency-attention-card-support-tickets',
    },
  ].filter((c) => (c.count || 0) > 0);

  if (cards.length === 0) {
    return (
      <section className="mb-8" data-testid="agency-attention-required-empty">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
          Attention Required
        </h2>
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-6 py-8 text-center">
          <p className="text-base font-heading font-semibold text-slate-800">
            Nothing needs attention <span aria-hidden="true">🎉</span>
          </p>
          <p className="text-sm text-slate-500 mt-1">
            Your agency has no open action items right now.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="mb-8" data-testid="agency-attention-required">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
        Attention Required
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <AttentionCard
            key={c.key}
            title={c.title}
            count={c.count}
            description={c.description}
            icon={c.icon}
            href={c.href}
            actionLabel={c.actionLabel}
            severity={c.severity}
            testId={c.testId}
          />
        ))}
      </div>
    </section>
  );
};

export default AgencyAttentionRequired;
